/**
 * Lead Assignment Panel
 * Admin interface for assigning leads to staff members
 * Part of Task Management module
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Tooltip,
  Alert,
  Skeleton,
  Checkbox,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import {
  useUnassignedLeads,
  useLeadAssignmentSummary,
  useTaskAssignees,
  useAssignLead,
  useBulkAssignLeads,
} from '../hooks/useTasks';

const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (priority) {
    case 'HIGH':
    case 'URGENT':
      return 'error';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'info';
    default:
      return 'default';
  }
};

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'NEW':
      return 'primary';
    case 'CONTACTED':
      return 'warning';
    case 'QUALIFIED':
      return 'success';
    case 'UNQUALIFIED':
    case 'LOST':
      return 'error';
    default:
      return 'default';
  }
};

const LeadAssignmentPanel: React.FC = () => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [assignMode, setAssignMode] = useState<'single' | 'bulk'>('single');
  const [singleLeadToAssign, setSingleLeadToAssign] = useState<string | null>(null);

  // API Hooks
  const { data: leadsResponse, isLoading: leadsLoading } = useUnassignedLeads({
    search: searchQuery || undefined,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  });
  const { data: summaryData, isLoading: summaryLoading } = useLeadAssignmentSummary();
  const { data: assignees } = useTaskAssignees();
  const assignLeadMutation = useAssignLead();
  const bulkAssignMutation = useBulkAssignLeads();

  const leads = leadsResponse?.data || [];
  const totalLeads = leadsResponse?.pagination?.total || 0;

  // Handlers
  const handleSelectLead = (leadId: string) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((lead) => lead.id));
    }
  };

  const handleOpenAssignDialog = (mode: 'single' | 'bulk', leadId?: string) => {
    setAssignMode(mode);
    if (mode === 'single' && leadId) {
      setSingleLeadToAssign(leadId);
    }
    setSelectedStaffId('');
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedStaffId) {
      toast.warning('Please select a staff member');
      return;
    }

    try {
      if (assignMode === 'single' && singleLeadToAssign) {
        await assignLeadMutation.mutateAsync({
          leadId: singleLeadToAssign,
          assignedToUserId: selectedStaffId,
        });
        toast.success('Lead assigned successfully');
      } else if (assignMode === 'bulk' && selectedLeads.length > 0) {
        await bulkAssignMutation.mutateAsync({
          leadIds: selectedLeads,
          assignedToUserId: selectedStaffId,
        });
        toast.success(`${selectedLeads.length} leads assigned successfully`);
        setSelectedLeads([]);
      }
      setAssignDialogOpen(false);
      setSingleLeadToAssign(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to assign lead(s)');
    }
  };

  // Summary Cards
  const renderSummaryCards = () => (
    <Grid container spacing={3} mb={3}>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {summaryLoading ? <Skeleton width={40} /> : summaryData?.unassignedCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unassigned Leads
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'warning.light' }}>
                <PersonAddIcon color="warning" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {summaryLoading ? <Skeleton width={40} /> : summaryData?.assignedCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assigned Leads
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'success.light' }}>
                <CheckCircleIcon color="success" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {summaryLoading ? <Skeleton width={40} /> : summaryData?.totalLeads || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Active Leads
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'primary.light' }}>
                <TrendingUpIcon color="primary" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {summaryLoading ? <Skeleton width={40} /> : summaryData?.staffSummary?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Staff
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'info.light' }}>
                <GroupIcon color="info" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Staff Workload Summary
  const renderStaffWorkload = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Staff Workload
        </Typography>
        <Grid container spacing={2}>
          {summaryData?.staffSummary?.map((staff) => (
            <Grid item xs={12} sm={6} md={4} key={staff.id}>
              <Paper
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {staff.name.charAt(0)}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="subtitle2">{staff.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {staff.role}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((staff.leadCount / 20) * 100, 100)}
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                      color={staff.leadCount > 15 ? 'error' : staff.leadCount > 10 ? 'warning' : 'primary'}
                    />
                    <Typography variant="caption" fontWeight="bold">
                      {staff.leadCount} leads
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  // Lead Table Row
  const renderLeadRow = (lead: any) => (
    <TableRow key={lead.id} hover>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedLeads.includes(lead.id)}
          onChange={() => handleSelectLead(lead.id)}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {lead.leadNo}
        </Typography>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {lead.contactName}
          </Typography>
          {lead.companyName && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {lead.companyName}
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box display="flex" flexDirection="column" gap={0.5}>
          {lead.email && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption">{lead.email}</Typography>
            </Box>
          )}
          {lead.phone && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption">{lead.phone}</Typography>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Chip size="small" label={lead.status} color={getStatusColor(lead.status)} />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={lead.priority}
          color={getPriorityColor(lead.priority)}
          icon={<FlagIcon />}
        />
      </TableCell>
      <TableCell>
        {lead.aiScore !== undefined && lead.aiScore !== null ? (
          <Box display="flex" alignItems="center" gap={1}>
            <LinearProgress
              variant="determinate"
              value={Number(lead.aiScore)}
              sx={{ width: 60, height: 6, borderRadius: 3 }}
              color={Number(lead.aiScore) >= 70 ? 'success' : Number(lead.aiScore) >= 40 ? 'warning' : 'error'}
            />
            <Typography variant="caption" fontWeight="bold">
              {Number(lead.aiScore).toFixed(0)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            N/A
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {format(new Date(lead.createdAt), 'MMM d, yyyy')}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenAssignDialog('single', lead.id)}
        >
          Assign
        </Button>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Staff Workload */}
      {summaryData?.staffSummary && summaryData.staffSummary.length > 0 && renderStaffWorkload()}

      {/* Search and Bulk Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          size="small"
          placeholder="Search unassigned leads..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        {selectedLeads.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<GroupIcon />}
            onClick={() => handleOpenAssignDialog('bulk')}
          >
            Assign {selectedLeads.length} Lead{selectedLeads.length > 1 ? 's' : ''}
          </Button>
        )}
      </Box>

      {/* Leads Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                  checked={leads.length > 0 && selectedLeads.length === leads.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Lead No</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Contact Info</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>AI Score</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leadsLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={9}>
                    <Skeleton height={50} />
                  </TableCell>
                </TableRow>
              ))
            ) : leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                  <CheckCircleIcon sx={{ fontSize: 64, color: 'success.light', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    All leads are assigned!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    There are no unassigned leads at the moment
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map(renderLeadRow)
            )}
          </TableBody>
        </Table>
        {totalLeads > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalLeads}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </TableContainer>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {assignMode === 'bulk'
            ? `Assign ${selectedLeads.length} Leads to Staff`
            : 'Assign Lead to Staff'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Staff Member</InputLabel>
            <Select
              value={selectedStaffId}
              label="Select Staff Member"
              onChange={(e) => setSelectedStaffId(e.target.value)}
            >
              {assignees?.map((staff) => (
                <MenuItem key={staff.id} value={staff.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                      {staff.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{staff.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {staff.email} - {staff.role}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {summaryData?.staffSummary && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                Current workload:
              </Typography>
              {selectedStaffId && (
                <Typography variant="body2">
                  {summaryData.staffSummary.find((s) => s.id === selectedStaffId)?.leadCount || 0} leads assigned
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedStaffId || assignLeadMutation.isPending || bulkAssignMutation.isPending}
          >
            {assignLeadMutation.isPending || bulkAssignMutation.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadAssignmentPanel;
